import { AlertTriangle, Info, Trash2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ReactDOM from 'react-dom';
import { devLog } from '../utils/logger';
import { useModalFocus } from '../utils/hooks/useModalFocus';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  loading?: boolean;
  /** Tekst på bekreft-knappen under lasting (f.eks. "Sletter...", "Sender..."). Default: "Behandler..." */
  loadingText?: string;
  /** When set (e.g. 10001), portal is shown above admin overlay (z-[10000]) */
  portalZIndex?: number;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Bekreft',
  cancelText = 'Avbryt',
  variant = 'info',
  loading = false,
  loadingText = 'Behandler...',
  portalZIndex,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: Trash2,
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          buttonBg: 'bg-red-600 hover:bg-red-700',
          borderColor: 'border-red-200',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconBg: 'bg-orange-100',
          iconColor: 'text-orange-600',
          buttonBg: 'bg-orange-600 hover:bg-orange-700',
          borderColor: 'border-orange-200',
        };
      case 'info':
      default:
        return {
          icon: Info,
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          buttonBg: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg',
          borderColor: 'border-blue-200',
        };
      case 'success':
        return {
          icon: Info,
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          buttonBg: 'bg-green-600 hover:bg-green-700',
          borderColor: 'border-green-200',
        };
    }
  };

  const styles = getVariantStyles();
  const Icon = styles.icon;

  useModalFocus(isOpen);

  if (isOpen) {
    devLog('ConfirmDialog: isOpen true, rendering portal to body', { isOpen });
  }

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div
          key="confirm-dialog"
          className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe"
          style={{ zIndex: portalZIndex ?? 'var(--z-modal)' }}
        >
          {/* Backdrop - inside portal so click-to-close works outside isolation contexts */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 talentfunn-modal-overlay backdrop-blur-md"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              disabled={loading}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Icon & Title */}
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-12 h-12 ${styles.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-6 h-6 ${styles.iconColor}`} />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50 active:scale-95 font-medium"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`flex-1 px-4 py-3 ${styles.buttonBg} text-white rounded-xl transition-all disabled:opacity-50 active:scale-95 font-medium`}
              >
                {loading ? loadingText : confirmText}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}